import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Check,
  X,
  Loader2,
  ShieldAlert,
  ExternalLink,
  Star,
  Info,
  HelpCircle,
  UserCheck,
  UserX,
  AlertTriangle,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Editor-friendly moderation queue.
// Non-technical editors need: readable degree titles, a "how this works"
// panel, plain-English trust badges, and a proper reject-reason dialog.
// The underlying flow is unchanged: GET /admin/reviews/pending, then
// POST /admin/reviews/{id}/approve|reject.
// ---------------------------------------------------------------------------

const HELP_DISMISS_KEY = "wua.admin.reviews.help_dismissed";

const REJECT_REASONS = [
  { value: "spam", label: "Spam or promotional" },
  { value: "abusive", label: "Abusive or offensive language" },
  { value: "off_topic", label: "Off-topic / not about the course" },
  { value: "duplicate", label: "Duplicate submission" },
  { value: "fake", label: "Looks fake / not a real student" },
  { value: "other", label: "Other" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

function friendlyQualification(q) {
  if (!q) return "";
  const map = {
    bachelor: "Bachelor's",
    bachelors: "Bachelor's",
    ug: "Undergraduate",
    master: "Master's",
    masters: "Master's",
    pg: "Postgraduate",
    phd: "PhD",
    doctorate: "PhD",
    associate: "Associate",
    foundation: "Foundation",
  };
  const key = String(q).toLowerCase().replace(/[^a-z]/g, "");
  return map[key] || q;
}

function HelpPanel({ onDismiss }) {
  return (
    <div
      data-testid="admin-reviews-help"
      className="mt-6 rounded-lg border border-[#4C1D95]/20 bg-[#4C1D95]/[0.04] p-5"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#4C1D95]/10 p-1.5 text-[#4C1D95]">
          <Info className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="font-editorial text-lg">How this page works</h2>
            <button
              onClick={onDismiss}
              data-testid="admin-reviews-help-dismiss"
              className="text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-800"
            >
              Got it, hide this
            </button>
          </div>
          <p className="mt-2 text-sm text-stone-700 leading-relaxed">
            Students submit reviews through survey links we send them. Nothing
            goes live until an editor gives it the OK. Skim each review below
            and hit <strong>Approve</strong> if it looks like a real, useful
            opinion, or <strong>Reject</strong> for spam, abuse, or anything
            off-topic. If you're not sure, click the degree name to open the
            course page in a new tab and check the context.
          </p>
          <ul className="mt-3 grid gap-1.5 text-xs text-stone-600 sm:grid-cols-2">
            <li className="flex items-center gap-2">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <UserCheck className="h-2.5 w-2.5" />
              </span>
              <span><strong>Real invite</strong> — we sent them the link</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <UserX className="h-2.5 w-2.5" />
              </span>
              <span><strong>Anonymous</strong> — no name provided</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                <AlertTriangle className="h-2.5 w-2.5" />
              </span>
              <span><strong>Duplicate device</strong> — same browser used twice</span>
            </li>
            <li className="flex items-center gap-2">
              <HelpCircle className="h-3.5 w-3.5 text-stone-400" />
              <span>Hover any tag for a plain-English tooltip</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function TrustBadge({ item }) {
  if (item.duplicate_signature) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="badge-duplicate"
            className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-rose-700 ring-1 ring-rose-200"
          >
            <AlertTriangle className="h-3 w-3" /> Duplicate device
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-stone-900 text-white">
          Two or more reviews in this queue came from the same browser. Could
          be the same person submitting multiple times — double-check before
          approving.
        </TooltipContent>
      </Tooltip>
    );
  }
  if (item.has_invite) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="badge-real-invite"
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-emerald-700 ring-1 ring-emerald-200"
          >
            <UserCheck className="h-3 w-3" /> Real invite
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-stone-900 text-white">
          This student used a survey link we sent them personally — higher
          trust than a walk-in submission.
        </TooltipContent>
      </Tooltip>
    );
  }
  if (item.is_anonymous) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="badge-anonymous"
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-amber-700 ring-1 ring-amber-200"
          >
            <UserX className="h-3 w-3" /> Anonymous
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-stone-900 text-white">
          The student didn't leave a name. Still valid — just verify the review
          looks specific and genuine.
        </TooltipContent>
      </Tooltip>
    );
  }
  return null;
}

function RejectDialog({ open, onOpenChange, onConfirm, busy }) {
  const [reason, setReason] = useState("spam");
  const [note, setNote] = useState("");
  const confirm = () => onConfirm({ reason, note: note.trim() || null });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial">Reject this review?</DialogTitle>
          <DialogDescription>
            Rejected reviews are hidden from students and won't count toward
            the course rating. Tell us why so we can spot patterns.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-underground-sc tracking-wider text-stone-600">
            Reason
          </label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="reject-reason-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REJECT_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value} data-testid={`reject-reason-${r.value}`}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="block text-xs font-underground-sc tracking-wider text-stone-600">
            Optional note (only visible to admins)
          </label>
          <textarea
            data-testid="reject-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={400}
            rows={3}
            placeholder="Anything worth remembering about this one…"
            className="w-full rounded-md border border-stone-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4C1D95]/30"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="reject-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={busy}
            data-testid="reject-confirm"
            className="bg-stone-800 hover:bg-stone-900 text-white"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reject review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReviews() {
  const { user, login } = useAuth();
  const [state, setState] = useState({
    loading: true,
    forbidden: false,
    items: [],
    hiddenTestCount: 0,
  });
  const [busyIds, setBusyIds] = useState({});
  const [showHelp, setShowHelp] = useState(() => {
    try {
      return localStorage.getItem(HELP_DISMISS_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const [showTestData, setShowTestData] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // review id

  const load = async (opts = {}) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = opts.includeTest ? { include_test: true } : {};
      const r = await api.get("/admin/reviews/pending", { params });
      setState({
        loading: false,
        forbidden: false,
        items: r.data?.items || [],
        hiddenTestCount: r.data?.hidden_test_count || 0,
      });
    } catch (err) {
      const status = err?.response?.status;
      setState({
        loading: false,
        forbidden: status === 401 || status === 403,
        items: [],
        hiddenTestCount: 0,
      });
    }
  };

  useEffect(() => {
    load({ includeTest: showTestData });
  }, [showTestData]);

  const approve = async (id) => {
    setBusyIds((s) => ({ ...s, [id]: true }));
    try {
      await api.post(`/admin/reviews/${id}/approve`);
      toast.success("Review approved — it's now live on the degree page.");
      await load({ includeTest: showTestData });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve that review.");
    } finally {
      setBusyIds((s) => ({ ...s, [id]: false }));
    }
  };

  const submitReject = async ({ reason, note }) => {
    const id = rejectTarget;
    if (!id) return;
    setBusyIds((s) => ({ ...s, [id]: true }));
    try {
      await api.post(`/admin/reviews/${id}/reject`, { reason, note });
      toast.success("Review rejected.");
      setRejectTarget(null);
      await load({ includeTest: showTestData });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reject that review.");
    } finally {
      setBusyIds((s) => ({ ...s, [id]: false }));
    }
  };

  const dismissHelp = () => {
    try { localStorage.setItem(HELP_DISMISS_KEY, "1"); } catch { /* noop */ }
    setShowHelp(false);
  };

  const items = state.items;
  const count = items.length;

  if (state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm">
        Loading the moderation queue…
      </div>
    );
  }

  if (state.forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Admin access required</h1>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed">
            This area is restricted to editors on the WhichUniversityAdvisor
            team.{" "}
            {user
              ? `Signed in as ${user.email}, but this account isn't on the admin list.`
              : "Please sign in with your admin Google account."}
          </p>
          {!user && (
            <Button
              onClick={login}
              className="mt-4 bg-[#4C1D95] hover:bg-[#3B0764] text-white"
              data-testid="admin-signin"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="admin-reviews-page">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="label-uppercase text-stone-500">Admin</div>
            <h1 className="mt-1 font-editorial text-3xl">Moderation queue</h1>
            <p className="mt-1 text-sm text-stone-600" data-testid="admin-reviews-count">
              {count === 0
                ? "You're all caught up."
                : `${count} review${count === 1 ? "" : "s"} waiting for your OK.`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!showHelp && (
              <button
                onClick={() => setShowHelp(true)}
                data-testid="admin-reviews-help-show"
                className="inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-stone-500 hover:text-stone-800"
              >
                <HelpCircle className="h-3.5 w-3.5" /> How this works
              </button>
            )}
            <Link
              to="/admin/surveys/invites"
              data-testid="admin-invites-link"
              className="text-xs font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
            >
              Survey invites →
            </Link>
          </div>
        </div>

        {showHelp && <HelpPanel onDismiss={dismissHelp} />}

        {state.hiddenTestCount > 0 && !showTestData && (
          <button
            onClick={() => setShowTestData(true)}
            data-testid="show-test-data"
            className="mt-4 text-[11px] font-underground-sc tracking-wider text-stone-400 hover:text-stone-700"
          >
            {state.hiddenTestCount} automated test entr{state.hiddenTestCount === 1 ? "y" : "ies"} hidden · show anyway
          </button>
        )}
        {showTestData && (
          <button
            onClick={() => setShowTestData(false)}
            data-testid="hide-test-data"
            className="mt-4 text-[11px] font-underground-sc tracking-wider text-stone-400 hover:text-stone-700"
          >
            Hide automated test entries
          </button>
        )}

        {count === 0 && (
          <div
            data-testid="admin-reviews-empty"
            className="mt-8 rounded-lg border border-dashed border-stone-300 bg-white p-10 text-center"
          >
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <Check className="h-5 w-5" />
            </div>
            <h3 className="mt-3 font-editorial text-lg">Inbox zero</h3>
            <p className="mt-1 text-sm text-stone-500">
              No reviews to moderate right now. New student submissions will
              appear here as they come in.
            </p>
          </div>
        )}

        <div className="mt-6 space-y-4">
          {items.map((r) => (
            <ReviewCard
              key={r.id}
              item={r}
              busy={!!busyIds[r.id]}
              onApprove={() => approve(r.id)}
              onReject={() => setRejectTarget(r.id)}
            />
          ))}
        </div>

        <RejectDialog
          open={!!rejectTarget}
          onOpenChange={(o) => !o && setRejectTarget(null)}
          onConfirm={submitReject}
          busy={rejectTarget ? !!busyIds[rejectTarget] : false}
        />
      </div>
    </TooltipProvider>
  );
}

function ReviewCard({ item, busy, onApprove, onReject }) {
  const [slugOpen, setSlugOpen] = useState(false);
  const title = item.degree_title || "Untitled course";
  const uni = item.university_name || "";
  const qual = friendlyQualification(item.qualification);
  const intake = item.start_year ? `${item.start_year} intake` : "";
  const subtitle = [qual, intake].filter(Boolean).join(" · ");

  return (
    <div
      data-testid={`admin-review-${item.id}`}
      className="rounded-lg border border-stone-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          {/* Human-readable header */}
          <Link
            to={`/degree/${item.degree_id}`}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-baseline gap-2 text-[#4C1D95] hover:underline"
            data-testid={`admin-review-degree-${item.id}`}
          >
            <span className="font-editorial text-lg leading-tight">
              {title}
            </span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
          </Link>
          <div className="mt-0.5 text-xs text-stone-600">
            {uni && <span className="font-medium">{uni}</span>}
            {uni && subtitle && <span className="text-stone-400"> · </span>}
            {subtitle && <span>{subtitle}</span>}
          </div>

          {/* Trust + meta row */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <TrustBadge item={item} />
            {item.is_test_data && (
              <span
                data-testid="badge-test-data"
                className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500 ring-1 ring-stone-200"
              >
                Test data
              </span>
            )}
            <span className="text-[11px] font-underground-sc tracking-wider text-stone-500">
              {item.reviewer_display_name || "Anonymous"}
            </span>
            {item.graduation_year && (
              <span className="text-[11px] font-underground-sc tracking-wider text-stone-400">
                · grad {item.graduation_year}
              </span>
            )}
            {item.invite_label && (
              <span className="text-[11px] font-underground-sc tracking-wider text-stone-400">
                · via {item.invite_label}
              </span>
            )}
            <span className="text-[11px] font-underground-sc tracking-wider text-stone-400">
              · submitted {timeAgo(item.created_at)}
            </span>
            {item.submitter_ip_hash && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-testid={`device-sig-${item.id}`}
                    className="text-[11px] font-underground-sc tracking-wider text-stone-400 border-b border-dotted border-stone-300 cursor-help"
                  >
                    · device sig {item.submitter_ip_hash.slice(0, 6)}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-stone-900 text-white">
                  Anonymous one-way code from the submitter's browser. Two
                  reviews sharing this code = likely the same person. We never
                  store the raw IP address.
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Ratings */}
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-stone-700">
            <span className="inline-flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              Overall <strong>{item.overall_rating}/5</strong>
            </span>
            {item.teaching_rating != null && (
              <span>Teaching {item.teaching_rating}/5</span>
            )}
            {item.value_rating != null && <span>Value {item.value_rating}/5</span>}
            {item.campus_rating != null && <span>Campus {item.campus_rating}/5</span>}
            {item.would_recommend != null && (
              <span className={item.would_recommend ? "text-emerald-700" : "text-stone-500"}>
                {item.would_recommend ? "Would recommend" : "Would not recommend"}
              </span>
            )}
          </div>

          {/* Review text */}
          {item.review && (
            <p className="mt-3 whitespace-pre-wrap border-l-2 border-stone-300 pl-3 text-sm text-stone-800">
              {item.review}
            </p>
          )}

          {/* Collapsible raw slug for the tech-curious */}
          <button
            onClick={() => setSlugOpen((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-stone-400 hover:text-stone-600"
            data-testid={`slug-toggle-${item.id}`}
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${slugOpen ? "rotate-180" : ""}`} />
            {slugOpen ? "Hide" : "Show"} technical ID
          </button>
          {slugOpen && (
            <div className="mt-1 font-mono text-[11px] text-stone-500 break-all">
              {item.degree_id}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex shrink-0 flex-col gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={onApprove}
            data-testid={`admin-review-approve-${item.id}`}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                <Check className="h-3.5 w-3.5 mr-1" /> Approve
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onReject}
            data-testid={`admin-review-reject-${item.id}`}
            className="border-stone-300 text-stone-700 hover:bg-stone-100"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Reject
          </Button>
        </div>
      </div>
    </div>
  );
}
